/**
 * @component HistoryAdminPanel
 * @description 회사 연혁 항목을 추가·수정·삭제하고 전체를 저장하는 관리자 패널입니다.
 * @usage AdminPage의 '연혁' 탭에서 렌더링됩니다.
 */

import { useCallback, useEffect, useState } from 'react';
import AdminToolbar from '../../components/admin/AdminToolbar';
import AdminRowButton from '../../components/admin/AdminRowButton';
import { useEditableList } from '../../hooks/useEditableList';
import { useSeedReset } from '../../hooks/useSeedReset';
import ModalPopup from '../../components/common/ModalPopup';
import * as adminApi from '../../services/adminApi';
import { AdminAuthError } from '../../services/adminApi';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

// [기술] 저장 형식 "YYYY.MM"을 연도/월 입력값으로 분해합니다.
function splitYearString(value) {
  const [year, month] = String(value || '').split('.');
  return { year: year || String(new Date().getFullYear()), month: month ? parseInt(month, 10) : 1 };
}

// [기술] 연도+월 숫자를 저장 형식 "YYYY.MM"으로 변환합니다.
function toYearString(year, month) {
  return `${year}.${String(month).padStart(2, '0')}`;
}

function makeEmptyRow() {
  return { year: String(new Date().getFullYear()), month: 1, text: '', highlight: false };
}

// [기능] 기존 편집과 초기화 결과 반영을 제공하는 관리자 패널입니다.
export default function HistoryAdminPanel({ onUnauthorized }) {
  const { rows, setRowsFromServer, updateRow, toggleDelete, addRow, removeRow, resetDraft, buildPayload, isDirty } =
    useEditableList(makeEmptyRow);

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // [기능] 서버 형식(year: "YYYY.MM")을 편집용 행(year, month 분리)으로 변환해 적재합니다.
  const applyServerItems = useCallback(
    (items) => {
      setRowsFromServer(
        (items || []).map((item) => {
          const { year, month } = splitYearString(item.year);
          // [비즈니스] highlight는 공개 페이지의 강조 표시에 쓰이므로 편집 중에도 값을 보존합니다.
          return { year, month, text: item.text, highlight: !!item.highlight };
        })
      );
    },
    [setRowsFromServer]
  );

  useEffect(() => {
    let isActive = true;
    adminApi
      .fetchHistory()
      .then((items) => {
        if (isActive) applyServerItems(items);
      })
      .catch((error) => {
        if (isActive) setErrorMessage(error.message);
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [applyServerItems]);

  // [기능] 삭제 표시를 제외한 모든 행을 서버 형식으로 바꿔 전체 저장합니다.
  const handleSave = async () => {
    setErrorMessage('');
    setIsSaving(true);
    try {
      // [비즈니스] 내용이 비어 있는 행은 저장하지 않습니다. 추가만 해 두고 입력하지 않은 행을 걸러냅니다.
      const payload = buildPayload((row) => {
        const text = String(row.text).trim();
        if (!text) return null;
        return { year: toYearString(row.year, row.month), text, highlight: !!row.highlight };
      });

      const saved = await adminApi.saveHistory(payload);
      // [비즈니스] 서버가 연도 내림차순으로 재정렬하므로, 저장 결과로 화면을 다시 구성해
      //            표시 순서를 서버와 일치시킵니다.
      applyServerItems(saved);
    } catch (error) {
      if (error instanceof AdminAuthError) {
        onUnauthorized();
        return;
      }
      setErrorMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const { isResetting, requestReset, dialogProps } = useSeedReset({
    target: 'history',
    isDirty: isDirty,
    // [기능] 초기화 응답으로 편집 상태를 교체하고 기존 오류를 지웁니다.
    onApplied: (data) => {
      setErrorMessage('');
      applyServerItems(data);
    },
    onError: setErrorMessage,
    onUnauthorized,
  });

  if (loading) return <p className="admin-loading">불러오는 중...</p>;

  return (
    <section className="admin-panel">
      <AdminToolbar
        title="연혁 관리"
        hint="항목을 직접 수정할 수 있습니다. 저장하면 연도 내림차순으로 정렬됩니다."
        errorMessage={errorMessage}
        isSaving={isSaving}
        isDirty={isDirty}
        onSave={handleSave}
        onReset={resetDraft}
        onSeedReset={requestReset}
        isResetting={isResetting}
      />

      <ul className="admin-rows">
        {rows.map((row) => (
          <li key={row._uid} className={`admin-row admin-row-history${row._deleted ? ' is-deleted' : ''}`}>
            <input
              type="number"
              className="admin-input admin-input-year"
              value={row.year}
              onChange={(e) => updateRow(row._uid, 'year', e.target.value)}
              disabled={row._deleted}
              aria-label="연도"
            />
            <select
              className="admin-input admin-input-month"
              value={row.month}
              onChange={(e) => updateRow(row._uid, 'month', Number(e.target.value))}
              disabled={row._deleted}
              aria-label="월"
            >
              {MONTHS.map((m) => (
                <option key={m} value={m}>{`${m}월`}</option>
              ))}
            </select>
            <input
              type="text"
              className="admin-input admin-input-text"
              value={row.text}
              onChange={(e) => updateRow(row._uid, 'text', e.target.value)}
              placeholder="연혁 내용"
              disabled={row._deleted}
              aria-label="연혁 내용"
            />
            <label className="admin-checkbox" title="공개 페이지에서 강조 표시">
              <input
                type="checkbox"
                checked={!!row.highlight}
                onChange={(e) => updateRow(row._uid, 'highlight', e.target.checked)}
                disabled={row._deleted}
              />
              <span>강조</span>
            </label>

            {/* [비즈니스] 아직 저장되지 않은 새 행은 되돌릴 내용이 없으므로 즉시 제거합니다. */}
            {row._isNew ? (
              <AdminRowButton variant="delete" title="행 제거" onClick={() => removeRow(row._uid)} />
            ) : (
              <AdminRowButton
                variant={row._deleted ? 'restore' : 'delete'}
                onClick={() => toggleDelete(row._uid)}
              />
            )}
          </li>
        ))}
      </ul>

      <button type="button" className="admin-btn admin-btn-ghost admin-add-btn" onClick={addRow}>
        + 연혁 추가
      </button>
      <ModalPopup {...dialogProps} />
    </section>
  );
}
